// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { FinancialCommitmentModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/FinancialCommitmentModel";
import { FinancialCommitmentDTO } from "Globals/Models/DTO/FinancialCommitmentDTO";
FinancialCommitmentModel;
// ViewModels
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";

export default class FinancialCommitmentViewModel extends ViewModelBase<FinancialCommitmentModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(financialCommitmentModel?: FinancialCommitmentModel) {
        super(financialCommitmentModel ? financialCommitmentModel : new FinancialCommitmentModel());
    }

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<FinancialCommitmentModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public stash = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            let model = new FinancialCommitmentModel();
            model.fromDto(this.model);
            let viewModel = new FinancialCommitmentViewModel(model);

            runInAction(() => {
                incomeDetails.financialCommitmentViewModels.push(viewModel);
                // triggers the auto-save.
                incomeDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (incomeDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.incomeDetailId = incomeDetailId;

            let dto: FinancialCommitmentDTO = this.model.toDto();
            const apiResult = await this.Post<FinancialCommitmentDTO>(AppUrls.Server.ApplicationForm.Upsert.FinancialCommitment, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("FinancialCommitment:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("MoneyGifter:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("incomeDetailId")) {
                runInAction(() => {
                    let index = incomeDetails.financialCommitmentViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        incomeDetails.financialCommitmentViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.incomeDetailId = incomeDetails.model.id ? incomeDetails.model.id : "";
                this.model.isDeleted = true;

                let upsertFinancialCommitment: FinancialCommitmentDTO = this.model.toDto();
                const apiResult = await this.Post<FinancialCommitmentDTO>(AppUrls.Server.ApplicationForm.Upsert.FinancialCommitment, upsertFinancialCommitment);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("FinancialCommitment:Upsert-Successful");
                    let model = new FinancialCommitmentModel();
                    model.fromDto(apiResult.payload);

                    const filtered = incomeDetails.financialCommitmentViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        incomeDetails.financialCommitmentViewModels = filtered;
                    });
                    this.model.resetModel();
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("FinancialCommitment:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<FinancialCommitmentModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public setDate(fieldName: keyof FieldType<FinancialCommitmentModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
    }

    @computed
    public get getFormattedPaymentEndDate(): string {
        return moment.utc(this.model.paymentEndDate).local().format("DD/MM/YYYY");
    }

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<FinancialCommitmentModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<FinancialCommitmentModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<FinancialCommitmentModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
